import React from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Divider,
  Chip,
  Stack,
  Avatar,
  Button,
  Link  
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import { Close as CloseIcon, Link as LinkIcon } from '@mui/icons-material';
import { format } from 'date-fns';
import type { SportEvent, Match } from '../../../../models';

interface EventDetailsDrawerProps {
  open: boolean;
  onClose: () => void;
  event: SportEvent | Match | null;
  platform: 'd11' | 'c11' | 'mt11' | 'f11' | 'cb11' | 'cricbuzz' | 'crex' | 'ai11' | 'v11' | 'espn' | 'fotmob';
}

const EventDetailsDrawer: React.FC<EventDetailsDrawerProps> = ({ open, onClose, event, platform }) => {
  if (!event) return null;

  const isAI11 = platform === 'ai11';
  const startTime = new Date(isAI11 ? (event as Match).start_date : (event as SportEvent).start_time);
  const status = isAI11 ? (event as Match).match_status : (event as SportEvent).status;

  const getStatusColor = (status: string): 'info' | 'warning' | 'success' | 'error' | 'default' => {
    switch (status) {
      case 'not_started':
        return 'info';
      case 'in_progress':
        return 'warning';
      case 'completed':
        return 'success';
      case 'abandoned':
      case 'postponed':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { width: { xs: '100%', sm: 400 } }
      }}
    >
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Event Details</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider sx={{ mb: 2 }} />

        {/* Series Info */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Series
          </Typography>
          <Typography variant="body1">
            {isAI11 ? `${(event as Match).series?.name} - ${(event as Match).series?.id}` : `${(event as SportEvent).series_name} - ${(event as SportEvent).series_id}`}
          </Typography>
          {isAI11 && (event as Match).series && (
            <Link
              href={`/series/${(event as Match).series.id}`}
              underline="hover"
              sx={{ display: 'flex', alignItems: 'center', mt: 1 }}
            >
              <LinkIcon fontSize="small" sx={{ mr: 0.5 }} />
              View Series Details
            </Link>
          )}
        </Box>

        {/* Teams */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Teams
          </Typography>
          <Stack spacing={2}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar
                src={(event as SportEvent).team1_flag}
                alt={(event as SportEvent).team1_name}
                sx={{ width: 40, height: 40 }}
              />
              <Box>
                <Typography variant="body1">
                  {isAI11 ? `${(event as Match).teams[0]?.name} - ${(event as Match).teams[0]?.id}` : `${(event as SportEvent).team1_name} - ${(event as SportEvent).team1_id}`}
                </Typography>
                {!isAI11 && (event as SportEvent).team1_full_name && (
                  <Typography variant="caption" color="text.secondary">
                    {(event as SportEvent).team1_full_name}
                  </Typography>
                )}
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar
                src={(event as SportEvent).team2_flag}
                alt={(event as SportEvent).team2_name}
                sx={{ width: 40, height: 40 }}
              />
              <Box>
                <Typography variant="body1">
                  {isAI11 ? `${(event as Match).teams[1]?.name} - ${(event as Match).teams[1]?.id}` : `${(event as SportEvent).team2_name} - ${(event as SportEvent).team2_id}`}
                </Typography>
                {!isAI11 && (event as SportEvent).team2_full_name && (
                  <Typography variant="caption" color="text.secondary">
                    {(event as SportEvent).team2_full_name}
                  </Typography>
                )}
              </Box>
            </Box>
          </Stack>
        </Box>

        {/* Match Info */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Match Information
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                Start Time
              </Typography>
              <Typography variant="body1">
                {format(startTime, 'MMM dd, yyyy hh:mm a')}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                Status
              </Typography>
              <Chip
                label={status?.replace('_', ' ').toUpperCase()}
                color={getStatusColor(status)}
                size="small"
              />
            </Grid>
            {!isAI11 && (event as SportEvent).venue && (
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  Venue
                </Typography>
                <Typography variant="body1">
                  {(event as SportEvent).venue}
                </Typography>
              </Grid>
            )}
            {!isAI11 && (event as SportEvent).format && (
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  Format
                </Typography>
                <Typography variant="body1">
                  {(event as SportEvent).format}
                </Typography>
              </Grid>
            )}
          </Grid>
        </Box>

        {/* Platform Specific Info */}
        {!isAI11 ? (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Platform Information
            </Typography>
            <Grid container spacing={2}>
              {(event as SportEvent).prize_pool && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    Prize Pool
                  </Typography>
                  <Typography variant="body1" color="primary">
                    {(event as SportEvent).prize_pool}
                  </Typography>
                </Grid>
              )}
              {(event as SportEvent).lineup_status && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    Lineup Status
                  </Typography>
                  <Typography variant="body1">
                    {(event as SportEvent).lineup_status}
                  </Typography>
                </Grid>
              )}
              {(event as SportEvent).match_id && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    Lineup Status
                  </Typography>
                  <Typography variant="body1">
                    <Link href={`/matches/${(event as SportEvent).match_id}`} underline="hover">
                      View Match [{(event as SportEvent).match_id}]
                    </Link>
                  </Typography>
                </Grid>
              )}
              {(event as SportEvent).match_series_id && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    Lineup Status
                  </Typography>
                  <Typography variant="body1">
                    <Link href={`/series/${(event as SportEvent).match_series_id}`} underline="hover">
                      View Series [{(event as SportEvent).match_series_id}]
                    </Link>
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Box>
        ) : (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Linked match information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    EO Id: {(event as Match).e_o_id}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    D Id: {(event as Match).d_id}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    C Id: {(event as Match).c_id}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    MT Id: {(event as Match).mt_id}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    V id: {(event as Match).v_id}
                  </Typography>
                </Grid>
            </Grid>
          </Box>
        )}

        {/* Actions */}
        <Box sx={{ mt: 3 }}>
          <Button
            variant="contained"
            fullWidth
            onClick={() => window.location.href = `/matches/${event.id}`}
          >
            View Match Details
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default EventDetailsDrawer; 